// The app-token exchange client (feature 2026-09-18-audience-bound-dp-token,
// WS-2 §3.1). One function, no state, no timer. The shell calls it when it
// opens a hosted app; the token it returns is the bearer that app's own routes
// carry, and the session token never reaches those routes again (D-1, D-3).
import { APP_TOKEN_ROUTE, appTokenResponseSchema, } from '@zion/sdk-contracts';
/**
 * The exchange did not yield a token. Thrown by {@link mintAppToken}, and by
 * nothing else.
 *
 * It carries no server text. The exchange answers one body per status
 * (`security-audit.md` C6), so repeating that body would invite a caller to
 * branch on a string the server deliberately keeps uniform. A caller that
 * needs to distinguish the cases reads `status`.
 */
export class AppTokenMintError extends Error {
    status;
    appId;
    /**
     * @param status The HTTP status the exchange answered with, `'network'` when
     *   the request never completed, or `'malformed'` when the 200 body failed
     *   `appTokenResponseSchema` or named another app.
     * @param appId The `release.apps_catalog` uuid the caller asked for, echoed
     *   so a caller that raced two exchanges knows which one failed.
     */
    constructor(status, appId) {
        super(`App token mint failed for ${appId}: ${status}`);
        this.status = status;
        this.appId = appId;
        this.name = 'AppTokenMintError';
    }
}
/**
 * Exchange the caller's session token for an access token bound to one app.
 *
 * `POST`s `{ appId }` to `APP_TOKEN_ROUTE` through `authFetch` with
 * `Content-Type: application/json`, and parses a 200 body with
 * `appTokenResponseSchema.parse`. The shell therefore never holds a token
 * whose shape it could not confirm.
 *
 * @param appId The `release.apps_catalog` uuid of the app to bind to, the
 *   value `ResolvedApp.appUuid` carries on a `GET /v1/apps` row. It is not the
 *   slug: `appTokenRequestSchema` requires a uuid and a slug is a 400.
 * @param authFetch The session-bearing fetch. Required, and it must attach the
 *   session bearer: `APP_TOKEN_ROUTE` sits under `/v1/auth`, which is a
 *   reserved platform prefix, and the exchange refuses an app token (C1 hop 1).
 * @returns The parsed `AppTokenResponse`. `expiresIn` is a positive integer of
 *   seconds, at most the session token's remaining lifetime, and `appId`
 *   equals the argument.
 * @throws {AppTokenMintError} with `'network'` when `authFetch` rejects,
 *   `'malformed'` when a 200 body does not parse as JSON, fails the schema
 *   (R-052) or names an `appId` other than the argument (R-055), and the
 *   response status for every other non-200. The store is never written on
 *   any of these.
 *
 * It retries nothing itself. `authFetch` owns the bounded 401 refresh-and-replay
 * and the bounded cold-start loop; the shell's `ensureAppToken` coalesces
 * concurrent callers. It holds no state, starts no timer, and its one side
 * effect is the request.
 */
export async function mintAppToken(appId, authFetch) {
    let response;
    try {
        response = await authFetch(APP_TOKEN_ROUTE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ appId }),
        });
    }
    catch {
        throw new AppTokenMintError('network', appId);
    }
    if (!response.ok) {
        throw new AppTokenMintError(response.status, appId);
    }
    let body;
    try {
        body = await response.json();
    }
    catch {
        throw new AppTokenMintError('malformed', appId);
    }
    const parsed = appTokenResponseSchema.safeParse(body);
    if (!parsed.success) {
        throw new AppTokenMintError('malformed', appId);
    }
    // The caller stores this bearer under the uuid it asked for, so a reply
    // naming another app would put app Y's token on app X's routes (R-055,
    // SEC-C2-2). The exchange echoes the request today; this is the
    // postcondition the docblock states, enforced.
    if (parsed.data.appId !== appId) {
        throw new AppTokenMintError('malformed', appId);
    }
    return parsed.data;
}
//# sourceMappingURL=app-token-client.js.map